import moment from 'moment';
import { getRecommendationsData } from '../getRecommendationsData';
import { PublicProvider } from '../../api/Providers';
import { ClientProviderProfile } from '../../api/ProfileClientView';
import nonCriticalException from '../exceptionLogger';
import * as KeyValueStorage from '../KeyValueStorage';
import { STYLIES_LOCALSTORAGE_CACHE_KEY } from './constants';
import type {
  StylieAwardData,
  StylieAwardsInfoType,
  StyliesLocalCache,
} from './types';

const getStyliesLocalCache = async () => {
  const {
    setDate,
    data: cachedData,
  }: StyliesLocalCache = (await KeyValueStorage.getItem(
    STYLIES_LOCALSTORAGE_CACHE_KEY,
  )) || ({} as StyliesLocalCache);
  if (setDate && moment(setDate).add(1, 'day').isBefore(moment.now())) {
    // Expire cache
    await KeyValueStorage.removeItem(STYLIES_LOCALSTORAGE_CACHE_KEY);
    return undefined;
  }
  return cachedData;
};

const setStyliesLocalCache = async (newStyliesData: StylieAwardsInfoType) => {
  const newCacheData: StyliesLocalCache = {
    data: newStyliesData,
    setDate: new Date(),
  };
  await KeyValueStorage.setItem(STYLIES_LOCALSTORAGE_CACHE_KEY, JSON.stringify(newCacheData));
};

class StyliesManager {
  private styliesData: StylieAwardsInfoType;

  public constructor() {
    this.styliesData = null;
  }

  private async getStylies(): Promise<StylieAwardsInfoType> {
    if (this.styliesData) {
      return this.styliesData;
    }

    const cachedStyliesData = await getStyliesLocalCache();
    if (cachedStyliesData) {
      this.styliesData = cachedStyliesData;
      return this.styliesData;
    }
    try {
      const newStyliesData = await getRecommendationsData<Promise<StylieAwardsInfoType>>('stylies', 'stylies');
      await setStyliesLocalCache(newStyliesData);
      this.styliesData = newStyliesData;
    } catch (error) {
      nonCriticalException(error);
      this.styliesData = {
        year: '',
        awards_data: [],
      };
    }
    return this.styliesData;
  }

  public async getAwardYear(): Promise<string> {
    const styliesData = await this.getStylies();
    return styliesData.year;
  }

  public async getProviderAward(
    profile: PublicProvider | ClientProviderProfile,
  ): Promise<StylieAwardData> {
    let awardData: StylieAwardData = {
      is_awardee: false,
    };

    const styliesData = await this.getStylies();

    styliesData.awards_data.forEach(award => {
      if (award.awardees[profile.id] !== undefined) {
        awardData = {
          is_awardee: true,
          award_type: award.award_name,
          award_service: award.awardees[profile.id],
        };
      }
    });
    return awardData;
  }
}

const Stylies = new StyliesManager();
export default Stylies;
